export interface ResponseBase {
  createdDate: number;
  modifiedDate: number;
  createdBy: string;
  modifiedBy: string;
  recDeleted: boolean;
  orgId: string;
  appId: string;
  sid: string;
  skipFieldsForValidation: any; //check
}

export interface IGenerationWizard extends ResponseBase {
  name: string;
  selectedFilesCount: number;
  prototypeCreationNeeded: boolean;
  automaticSteps: string[];
  generationComponents: string[];
  description: string;
  approachType: string;
  status: GenerationStatus;
  availableSteps: string[];
  currentStep: string;
}

export interface IGenerationStep extends ResponseBase {
  generationId: string;
  stepData: Record<string, any>; //check
  stepStatus: GenerationStepStatus;
  stepName: string;
  userPrompt: string;
  component: boolean;
}

export interface StepComponent {
  beforeNext(): Promise<boolean>;
  canProceed?: boolean; // Optional: child component can control Next button disabled state
}

export enum GenerationStepPascalCase {
  Module = 'Module',
  Process = 'Process',
  UserStory = 'UserStory',
  DataModel = 'DataModel',
  Role = 'Role',
  AclPermission = 'AclPermission',
  Workflow = 'Workflow',
  BusinessLogic = 'BusinessLogic',
  Page = 'Page',
  Integration = 'Integration',
  Complete = 'Complete'
}

export enum GenerationStepCapitals {
  SELECT = 'SELECT',
  INPUT = 'INPUT',
  ANALYZE = 'ANALYZE',
  MODULE = 'MODULE',
  PROCESS = 'PROCESS',
  USER_STORY = 'USER_STORY',
  DATA_MODEL = 'DATA_MODEL',
  ROLE = 'ROLE',
  ACL_PERMISSION = 'ACL_PERMISSION',
  WORKFLOW = 'WORKFLOW',
  BUSINESS_LOGIC = 'BUSINESS_LOGIC',
  PAGE = 'PAGE',
  INTEGRATION = 'INTEGRATION',
  COMPLETE = 'COMPLETE'
}

export enum GenerationStepUIName {
  SELECT = 'Select',
  INPUT = 'Input',
  ANALYZE = 'Analyze',
  MODULE = 'Modules',
  PROCESS = 'Processes',
  USER_STORY = 'User stories',
  DATA_MODEL = 'Data models',
  ROLE = 'Roles',
  WORKFLOW = 'Workflows',
  BUSINESS_LOGIC = 'Business Logics',
  PAGE = 'Pages',
  INTEGRATION = 'Integrations',
  COMPLETE = 'Complete',
  ACL_PERMISSION = 'ACL Permissions'
}

export enum GenerationStepStatus {
  YET_TO_START = 'YET_TO_START',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED_RETRY = 'FAILED_RETRY',
  GENERATION_COMPLETED = 'GENERATION_COMPLETED',
  FILE_INGESTION_COMPLETED = 'FILE_INGESTION_COMPLETED'
}

export enum GenerationStatus {
  YET_TO_START = 'YET_TO_START',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  MERGE_FAILED = 'MERGE_FAILED',
  MERGE_COMPLETED_AND_PROTOTYPE_FAILED = 'MERGE_COMPLETED_AND_PROTOTYPE_FAILED'
}

export enum NodeType {
  PROCESS = 'PROCESS',
  MODULE = 'MODULE',
  USER_STORY = 'USER_STORY'
}

export enum GenerationType {
  AI = 'AI',
  MANUAL = 'MANUAL'
}

export type ApprovalStatus = 'APPROVED' | 'REJECTED';
export type MoscowValue = 'MUST' | 'SHOULD' | 'COULD' | 'WISH';
export type ScopeValue = 'IN' | 'OUT' | 'TBD';
export type DataImpactValue = 'None' | 'Read-Only' | 'Critical Write' | 'Major Update';
export type CognitiveLoadValue = 'Low' | 'Medium' | 'High';
export type UsageFrequencyValue = 'Daily' | 'Weekly' | 'Monthly' | 'Rarely';

export interface UserStoryNodeBase {
  type: NodeType;
  name: string;
  shortName: string;
  description: string | null;
  parentNodeSid: string | null;
  previousNodeSid?: string | null;
  aiConfidence: number | null;
  // pageNumber?: number | null;
  source: string | null;
  referredRoles: string[];
  generationType: GenerationType;
  approvalStatus: ApprovalStatus;
  generationId: string;
  placeholder: boolean;
  priority: number;
  complexity: number;
  moscow: MoscowValue;
  scope: ScopeValue;
  estimation: number;
  preConditions: string | null;
  basicFlow: string | null;
  alternateFlows: string | null;
  successCriteria: string | null;
  automationSuggestion: string | null;
  agentSuggestion: string | null;
  workflowStage: string | null;
  usageFrequency: UsageFrequencyValue;
  dataImpact: DataImpactValue;
  cognitiveLoad: CognitiveLoadValue;
  dirty: boolean; // UI-only field, not persisted to backend
  module: string | null; // UI-only field, not persisted to backend
}

export interface IUserStory extends UserStoryBase, ResponseBase {}
export interface FileIngestionRAGModel extends ResponseBase {
  fileName: string;
  fileType: string;
  objectName: string;
  bucketName: string;
  fileSizeInBytes: number;
  status: RagFileIngestionStatus;
  evaAttachmentSId: string;
  ingestionPercentage: number;
  failureReason: string | null;
  metadata: Record<string, any> | null;
  userPrompt: string;
  uiFailureMessage: string;
  // generationIds: string[];
}

export enum RagFileIngestionStatus {
  YET_TO_START = 'YET_TO_START',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED'
}
export interface TreeNode {
  name: string;
  type: string;
  children?: TreeNode[];
  id?: string;
  hideProperties?: boolean;
  value?: {
    shortName?: string;
    description?: string;
    aiConfidence?: number;
    source?: string;
    // pageNumber?: number;
    //fields for userstories
    module?: string;
    epic?: string;
    roles?: ReferredRoles[];
    priority?: number;
    complexity?: number;
    workflowStage?: string;
    usageFrequency?: string;
    automation?: boolean;
    basicFlow?: string;
    alternateFlows?: string;
    successCriteria?: string;
    acceptanceCriteria?: string;
    dataImpact?: string;
    cognitiveLoad?: string;
    automationSuggestion?: string;
    moscow?: string;
    scope?: string;
    estimation?: number;
    summary?: string;
    preConditions?: string;
    agentSuggestion?: string;
    generationType?: string;
    identifier?: string;
    tableName?: string;
    actionFlows?: { action: string; fromLabel: string; toLabel: string }[];
    workflowId?: string;
    tableId?: string;
    generationId?: string;
  };
}

export interface ReferredRoles {
  sid: string;
  name: string;
}

export interface UserStoryBase {
  type: string;
  name: string;
  shortName: string;
  description: string | null;
  parentNodeSid: string | null;
  previousNodeSid?: string | null;
  referredRoles: string[];
  placeholder: boolean;
  priority: number;
  complexity: number;
  moscow: MoscowValue;
  scope: ScopeValue;
  estimation: number;
  preConditions: string | null;
  basicFlow: string | null;
  successCriteria: string | null;
  workflowStage: string | null;
  usageFrequency: UsageFrequencyValue;
  dataImpact: DataImpactValue;
  cognitiveLoad: CognitiveLoadValue;
  summary: string | null;
  automation: boolean;
  acceptanceCriteria: string | null;
  automationSuggestion: string | null;
  agentSuggestion: string | null;
  alternateFlows: string | null;
}

export interface SystemUserStoryBase extends UserStoryBase {}

export interface GenerationUserStory extends UserStoryBase {
  aiConfidence: number | null;
  // pageNumber?: number | null;
  source: string | null;
  generationType: GenerationType;
  generationId: string;
  approvalStatus: ApprovalStatus;
  dirty: boolean; // UI-only field, not persisted to backend
  module: string | null; // UI-only field, not persisted to backend
  identifier: string | null;
}
